/**
 * batch-38 garagefacturen (FO-329) — gedeelde types + Zod-schemas voor de
 * automatische onderhoudsregistratie uit garagefacturen, de werkvoorraad
 * "onderhoudsfactuur te koppelen" en de backfill met preview.
 *
 * Leaf-module: importeert alleen andere leaf-modules (kostendrager,
 * inkoop-kostensoort, voertuigen) — geen circulaire imports op `inkoop.ts`.
 */

import { z } from 'zod'
import { VOERTUIG_ONDERHOUD_TYPE, type VoertuigOnderhoudType } from './voertuigen'
import { KOSTENDRAGER_DOELTYPE, type KostendragerDoeltype } from './kostendrager'
import { idParam } from './id-validators'
import { ONDERHOUD_CLASSIFICATIE_STAMCODE, type KostensoortRefDTO } from './kostensoorten'

// ─── Regel-classificatie (AI-extractie per factuurregel) ─────────────────────

export const ONDERHOUD_REGEL_CLASSIFICATIE = [
  'BEURT_KLEIN',
  'BEURT_GROOT',
  'REPARATIE',
  'APK_KEURING',
  'BANDEN',
  'SCHADE',
  'OVERIG',
  // batch-68 werkplaatsfacturen (FO-341 §3.3) - klein onderhoud / bijvullen
  // (ruitenwisser, interieurfilter, vloeistoffen, lampje): onderdeel van het
  // bezoek, telt als onderhoud, maar reset op zichzelf geen interval.
  'KLEIN_ONDERHOUD',
] as const
export type OnderhoudRegelClassificatie = (typeof ONDERHOUD_REGEL_CLASSIFICATIE)[number]

export const onderhoudRegelClassificatieSchema = z.enum(ONDERHOUD_REGEL_CLASSIFICATIE)

export const ONDERHOUD_REGEL_CLASSIFICATIE_LABELS: Record<OnderhoudRegelClassificatie, string> = {
  BEURT_KLEIN: 'Kleine beurt',
  BEURT_GROOT: 'Grote beurt',
  REPARATIE: 'Reparatie',
  APK_KEURING: 'APK/keuring',
  BANDEN: 'Banden',
  SCHADE: 'Schade',
  OVERIG: 'Overig (geen onderhoud)',
  KLEIN_ONDERHOUD: 'Klein onderhoud / bijvullen',
}

/**
 * Classificaties die als "onderhoud uitgevoerd" tellen (FO §12): minstens één
 * regel in deze categorieën = onderhoudsregistratie. Puur OVERIG (accessoires,
 * wasstraat) levert geen automatische logregel op.
 */
export const ONDERHOUD_TELLENDE_CLASSIFICATIES: readonly OnderhoudRegelClassificatie[] = [
  'BEURT_KLEIN',
  'BEURT_GROOT',
  'REPARATIE',
  'APK_KEURING',
  'BANDEN',
  'SCHADE',
  'KLEIN_ONDERHOUD',
]

export function isOnderhoudClassificatie(c: OnderhoudRegelClassificatie): boolean {
  return c !== 'OVERIG'
}

/** Mapping classificatie → onderhoudslog-type (VoertuigOnderhoud.type). */
export function classificatieNaarOnderhoudType(
  c: OnderhoudRegelClassificatie,
): VoertuigOnderhoudType {
  switch (c) {
    case 'BEURT_KLEIN':
    case 'BEURT_GROOT':
      return 'ONDERHOUDSBEURT'
    case 'REPARATIE':
      return 'REPARATIE'
    case 'APK_KEURING':
      return 'APK'
    case 'BANDEN':
      return 'BANDEN'
    case 'SCHADE':
      return 'SCHADE'
    // batch-68 (FO-341 §3.3) - alleen bijvullen/klein onderhoud is geen beurt:
    // de logregel heet "overig" (geen FO-327-signaalsluiting, geen reset).
    case 'KLEIN_ONDERHOUD':
    case 'OVERIG':
      return 'OVERIG'
  }
}

/**
 * Bepaal het logregel-type voor een set regels (één factuur × één kenteken)
 * met meerdere classificaties: de "zwaarste" wint. Een grote/kleine beurt
 * domineert (die verklaart de FO-327-reset), daarna reparatie, schade, banden
 * en als laatste APK (een losse APK-regel naast een beurt is een bijzaak; een
 * APK-classificatie triggert los hiervan het keuringsdatum-voorstel).
 */
const TYPE_PRIORITEIT: readonly OnderhoudRegelClassificatie[] = [
  'BEURT_GROOT',
  'BEURT_KLEIN',
  'REPARATIE',
  'SCHADE',
  'BANDEN',
  'APK_KEURING',
  // batch-68 (FO-341) - alleen als er niets zwaarders op de set staat.
  'KLEIN_ONDERHOUD',
]

export function bepaalOnderhoudTypeVoorSet(
  classificaties: readonly OnderhoudRegelClassificatie[],
): VoertuigOnderhoudType | null {
  for (const c of TYPE_PRIORITEIT) {
    if (classificaties.includes(c)) return classificatieNaarOnderhoudType(c)
  }
  return null
}

/**
 * Mapping classificatie → stamtabel-code van de kostensoort (batch-65 FO-482; was
 * de FO-320-enum). OVERIG = geen eenduidige soort (null, nooit gokken).
 */
export function classificatieNaarKostensoort(c: OnderhoudRegelClassificatie): string | null {
  return ONDERHOUD_CLASSIFICATIE_STAMCODE[c] ?? null
}

// ─── Status-enums (spiegelen de Prisma-enums) ────────────────────────────────

export const GARAGE_WERKVOORRAAD_STATUS = ['OPEN', 'GEKOPPELD', 'GEEN_VOERTUIG'] as const
export type GarageWerkvoorraadStatus = (typeof GARAGE_WERKVOORRAAD_STATUS)[number]

export const GARAGE_WERKVOORRAAD_STATUS_LABELS: Record<GarageWerkvoorraadStatus, string> = {
  OPEN: 'Open',
  GEKOPPELD: 'Gekoppeld',
  GEEN_VOERTUIG: 'Geen STREEP-voertuig',
}

export const GARAGE_BACKFILL_RUN_STATUS = ['ACTIEF', 'AFGEROND', 'GEANNULEERD'] as const
export type GarageBackfillRunStatus = (typeof GARAGE_BACKFILL_RUN_STATUS)[number]

export const GARAGE_BACKFILL_FACTUUR_STATUS = [
  'PENDING',
  'PREVIEW',
  'BEVESTIGD',
  'OVERGESLAGEN',
  'WERKVOORRAAD',
] as const
export type GarageBackfillFactuurStatus = (typeof GARAGE_BACKFILL_FACTUUR_STATUS)[number]

export const GARAGE_BACKFILL_FACTUUR_STATUS_LABELS: Record<GarageBackfillFactuurStatus, string> = {
  PENDING: 'Nog te analyseren',
  PREVIEW: 'Te beoordelen',
  BEVESTIGD: 'Bevestigd',
  OVERGESLAGEN: 'Overgeslagen',
  WERKVOORRAAD: 'Naar werkvoorraad',
}

export const GARAGE_ONDERHOUD_FACTUUR_ROL = ['BRON', 'DEELFACTUUR', 'CREDITNOTA'] as const
export type GarageOnderhoudFactuurRol = (typeof GARAGE_ONDERHOUD_FACTUUR_ROL)[number]

export const GARAGE_ONDERHOUD_FACTUUR_ROL_LABELS: Record<GarageOnderhoudFactuurRol, string> = {
  BRON: 'Bronfactuur',
  DEELFACTUUR: 'Deelfactuur',
  CREDITNOTA: 'Creditnota',
}

export const GARAGE_APK_VOORSTEL_STATUS = ['OPEN', 'BEVESTIGD', 'AFGEWEZEN'] as const
export type GarageApkVoorstelStatus = (typeof GARAGE_APK_VOORSTEL_STATUS)[number]

export const VOERTUIG_ONDERHOUD_BRON = ['HANDMATIG', 'GARAGEFACTUUR'] as const
export type VoertuigOnderhoudBron = (typeof VOERTUIG_ONDERHOUD_BRON)[number]

// ─── Analyse-/preview-DTO's ──────────────────────────────────────────────────

/** Uitkomst per (factuur × kenteken)-set van de analyse. */
export const GARAGE_SET_UITKOMST = [
  'AUTOMATISCH', // eenduidige match: kan automatisch geregistreerd worden
  'AL_GEREGISTREERD', // koppeling (factuur, voertuig) bestaat al — idempotente no-op
  'BESTAANDE_LOGREGEL', // suggestie: koppel aan een bestaande logregel (deelfactuur/handmatig)
  'CREDITNOTA', // correctie: negatieve kostenkoppeling op een bestaande logregel
  'WERKVOORRAAD', // geen/ambigu/onbekend kenteken, alleen-overig of credit zonder doelregel
  // batch-62 garage-voertuig (FO-465 §7/§5.4) - tweede factuur bij een al
  // afgeronde afspraak (deel- + eindfactuur): het bedrag telt op bij dezelfde
  // onderhoudsregel, één keten - automatisch, nooit een tweede historie-regel.
  'AANVULLING_AFSPRAAK',
  // batch-68 werkplaatsfacturen (FO-341 §3.6) - een (deel)factuur op een
  // werkorder die al een registratie heeft (zelfde leverancier + werkorder):
  // automatisch als deelfactuur op die registratie, geen tweede beurt en geen
  // tweede reset.
  'ZELFDE_WERKORDER',
] as const
export type GarageSetUitkomst = (typeof GARAGE_SET_UITKOMST)[number]

// ─── batch-68 werkplaatsfacturen (FO-341) — werkvoorraadreden + secties ──────

/**
 * Waarom een werkplaatsfactuur niet automatisch geregistreerd werd (spiegelt de
 * Prisma-enum `GarageWerkvoorraadReden`). Elke reden heeft een eigen
 * bevestig-dialoog; OVERIG = de bestaande FO-329-redenen (alleen tekst).
 */
export const GARAGE_WERKVOORRAAD_REDEN = [
  'ONBEKEND_VOERTUIG',
  'KENTEKEN_VIN_CONFLICT',
  'LAGE_ZEKERHEID',
  'DATUM_ONWAARSCHIJNLIJK',
  'OVERIG',
] as const
export type GarageWerkvoorraadReden = (typeof GARAGE_WERKVOORRAAD_REDEN)[number]

export const GARAGE_WERKVOORRAAD_REDEN_LABELS: Record<GarageWerkvoorraadReden, string> = {
  ONBEKEND_VOERTUIG: 'Onbekend voertuig',
  KENTEKEN_VIN_CONFLICT: 'Kenteken en chassisnummer spreken elkaar tegen',
  LAGE_ZEKERHEID: 'Classificatie niet zeker genoeg',
  DATUM_ONWAARSCHIJNLIJK: 'Werkdatum onwaarschijnlijk',
  OVERIG: 'Handmatig koppelen',
}

/** Hoe het voertuig gevonden is (FO-341 §3.2). */
export const WERKPLAATS_MATCH_VIA = ['KENTEKEN', 'KENTEKEN_VIN', 'VIN'] as const
export type WerkplaatsMatchVia = (typeof WERKPLAATS_MATCH_VIA)[number]

export const WERKPLAATS_MATCH_VIA_LABELS: Record<WerkplaatsMatchVia, string> = {
  KENTEKEN: 'Gevonden op kenteken',
  KENTEKEN_VIN: 'Kenteken en chassisnummer kloppen',
  VIN: 'Gevonden op chassisnummer',
}

/** Bron van een sectieclassificatie (FO-341 §3.3). */
export const WERKPLAATS_SECTIE_BRON = ['TREFWOORD', 'AI', 'REGELS', 'HANDMATIG'] as const
export type WerkplaatsSectieBron = (typeof WERKPLAATS_SECTIE_BRON)[number]

export const WERKPLAATS_SECTIE_BRON_LABELS: Record<WerkplaatsSectieBron, string> = {
  TREFWOORD: 'Trefwoord van de garage',
  AI: 'Automatisch herkend',
  REGELS: 'Afgeleid uit de regels',
  HANDMATIG: 'Gecorrigeerd in de werkvoorraad',
}

/** Minimale zekerheid voor automatisch registreren (FO-341 §3.6). */
export const WERKPLAATS_CLASSIFICATIE_DREMPEL = 0.8

/**
 * Eén sectie van een werkplaatsfactuur (A, B, C, ...) zoals de analyse hem
 * indeelde. Bedragen zijn alleen gevuld voor wie kosten mag zien (de API leegt
 * ze anders); in de opgeslagen extractie staan ze altijd.
 */
export interface WerkplaatsSectieDTO {
  code: string | null
  titel: string
  classificatie: OnderhoudRegelClassificatie
  bron: WerkplaatsSectieBron
  /** 0..1; TREFWOORD = 1. */
  zekerheid: number
  /** Het trefwoord dat de sectie vastlegde (bron TREFWOORD), anders null. */
  trefwoord: string | null
  /** Arbeidsregels (werkzaamheden): code + omschrijving. */
  arbeid: Array<{ code: string | null; omschrijving: string }>
  /** Onderdelen: code + omschrijving + aantal. */
  onderdelen: Array<{ code: string | null; omschrijving: string; aantal: string | null }>
  /** Bedragen als canonieke decimale string ("243.38"); null = onbekend of afgeschermd. */
  arbeidBedrag: string | null
  onderdelenBedrag: string | null
  subtotaal: string | null
}

export interface GarageOnderhoudVoertuigMatchDTO {
  voertuigId: string
  kenteken: string
  merk: string
  model: string
  /** false = verkocht/afgeschreven (naboeking mag, met waarschuwing). */
  actief: boolean
}

export interface GarageOnderhoudRegelVoorstelDTO {
  regelId: string
  positie: number
  omschrijving: string
  bedrag: number
  classificatie: OnderhoudRegelClassificatie
}

export interface GarageOnderhoudSetPreviewDTO {
  /** Genormaliseerd kenteken (zonder streepjes); '' = geen kenteken gevonden. */
  kenteken: string
  /** Kenteken zoals op de factuur vermeld (weergave), of null. */
  kentekenWeergave: string | null
  /** Eenduidige voertuig-match, of null (0 of >1 kandidaten). */
  voertuig: GarageOnderhoudVoertuigMatchDTO | null
  /** Aantal kandidaat-voertuigen voor dit kenteken (0/1/n). */
  kandidaten: number
  regels: GarageOnderhoudRegelVoorstelDTO[]
  kmStand: number | null
  /** Datum voor de logregel (ISO, werkdatum met factuurdatum-fallback). */
  werkDatum: string | null
  werkDatumBron: 'WERKDATUM' | 'FACTUURDATUM'
  /** Expliciet op de factuur vermelde nieuwe APK-vervaldatum (ISO), of null. */
  apkVervaldatum: string | null
  /** Voorgesteld logregel-type (null = geen onderhoud in deze set). */
  type: VoertuigOnderhoudType | null
  /** Voorgestelde samengevatte omschrijving. */
  omschrijving: string | null
  /** Som van de regelbedragen van deze set (excl. btw; negatief bij credit). */
  bedrag: number
  uitkomst: GarageSetUitkomst
  /** Menselijke uitleg (waarom werkvoorraad, waarschuwingen). */
  redenen: string[]
  /** Bij BESTAANDE_LOGREGEL/CREDITNOTA: de bestaande doel-logregel. */
  bestaandeOnderhoudId: string | null
  /**
   * True = AUTOMATISCH zonder waarschuwingen: mag in de backfill via de
   * "alles eenduidige bevestigen"-bulk.
   */
  eenduidig: boolean
  /**
   * batch-59 voertuig-afspraken (FO-435 stap 6) — voertuig-afspraken die binnen
   * het matching-venster rond de werkdatum liggen. Bij precies één kandidaat
   * koppelt de keten automatisch; staan er meerdere (of geen eenduidige
   * voertuig-match), dan zijn dit de SUGGESTIES in de review-werkvoorraad zodat
   * de behandelaar niet zelf hoeft te zoeken. Optioneel: items van vóór deze
   * batch hebben het veld niet.
   */
  afspraakSuggesties?: GarageAfspraakSuggestieDTO[]

  // ── batch-68 werkplaatsfacturen (FO-341) — optioneel: oudere items missen ze ──
  /** Gestructureerde reden bij WERKVOORRAAD (null = automatisch of oud item). */
  redenCode?: GarageWerkvoorraadReden | null
  /** Chassisnummer (VIN) van de factuur, genormaliseerd. */
  vin?: string | null
  /** Hoe het voertuig gevonden is; null = geen eenduidige match. */
  matchVia?: WerkplaatsMatchVia | null
  /** Het lege chassisnummer van het voertuig wordt bij registreren gevuld. */
  chassisNummerVullen?: boolean
  /** Het voertuig dat het VIN aanwijst bij een kenteken/VIN-conflict. */
  vinVoertuig?: GarageOnderhoudVoertuigMatchDTO | null
  /** Gele waarschuwingen (merk, datum deel I); blokkeren niets. */
  plausibiliteit?: string[]
  /** Werkordernummer van het garagebezoek. */
  werkorderNummer?: string | null
  /** Laagste zekerheid van de classificatie die het type/de reset bepaalt (0..1). */
  classificatieZekerheid?: number | null
  /** De secties van de factuur die bij dit voertuig horen. */
  secties?: WerkplaatsSectieDTO[]
}

/** batch-59 (FO-435 stap 6) — afspraak-kandidaat bij een garagefactuur. */
export interface GarageAfspraakSuggestieDTO {
  afspraakId: string
  soort: 'ONDERHOUD' | 'SCHADE' | 'APK' | 'REPARATIE'
  status: string
  /** ISO-dag. */
  afspraakDatum: string | null
  garageNaam: string | null
  /** Absoluut verschil in dagen tussen afspraakdatum en werkdatum. */
  dagenVerschil: number | null
  terugroepReferentie: string | null
}

export interface GarageFactuurPreviewDTO {
  factuurId: string
  factuurNr: string
  leverancierNaam: string
  /** ISO-datum. */
  factuurdatum: string
  /** Factuurtotaal excl. btw. */
  bedrag: number
  isCreditnota: boolean
  sets: GarageOnderhoudSetPreviewDTO[]
  /** Waar de analyse vandaan kwam. */
  analyseBron: 'EXTRACTIE' | 'HERANALYSE_AI' | 'DETERMINISTISCH'
  /** Drift-guard (FO-303-P3): bevestigen eist een matchende token. */
  previewToken: string
  /** Analyse-fout (bv. AI-extractie mislukt); sets is dan leeg. */
  foutmelding: string | null
}

// ─── Werkvoorraad-DTO ────────────────────────────────────────────────────────

export interface GarageWerkvoorraadItemDTO {
  id: string
  status: GarageWerkvoorraadStatus
  factuurId: string
  factuurNr: string
  leverancierNaam: string
  /** ISO-datum. */
  factuurdatum: string
  /** Factuurtotaal excl. btw. */
  factuurBedrag: number
  /** Genormaliseerd kandidaat-kenteken; '' = geen kenteken gevonden. */
  kenteken: string
  reden: string
  /** batch-68 (FO-341) — gestructureerde reden; null = oud item (alleen tekst). */
  redenCode: GarageWerkvoorraadReden | null
  /** Voorstel uit de analyse (voor de koppel-actie), of null. */
  voorstel: GarageOnderhoudSetPreviewDTO | null
  voertuig: GarageOnderhoudVoertuigMatchDTO | null
  onderhoudId: string | null
  afgehandeldDoorNaam: string | null
  afgehandeldOp: string | null
  afhandelNotitie: string | null
  createdAt: string
}

// ─── Backfill-DTO's ──────────────────────────────────────────────────────────

export interface GarageBackfillTellingDTO {
  totaal: number
  pending: number
  preview: number
  bevestigd: number
  overgeslagen: number
  werkvoorraad: number
}

export interface GarageBackfillRunDTO {
  id: string
  status: GarageBackfillRunStatus
  /** ISO-datums (periode-filter op factuurdatum), of null. */
  periodeVan: string | null
  periodeTot: string | null
  gestartDoorNaam: string | null
  gestartOp: string
  afgerondOp: string | null
  afwijzingReden: string | null
  telling: GarageBackfillTellingDTO
}

export interface GarageBackfillItemDTO {
  id: string
  status: GarageBackfillFactuurStatus
  factuurId: string
  factuurNr: string
  leverancierNaam: string
  /** ISO-datum. */
  factuurdatum: string
  /** Factuurtotaal excl. btw. */
  bedrag: number
  preview: GarageFactuurPreviewDTO | null
  foutmelding: string | null
  notitie: string | null
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
}

export interface GarageBackfillRapportFactuurDTO {
  factuurNr: string
  leverancierNaam: string
  factuurdatum: string
  bedrag: number
  status: GarageBackfillFactuurStatus
  kentekens: string[]
  voertuigen: string[]
  notitie: string | null
}

export interface GarageBackfillRapportDTO {
  afgerondOp: string
  periodeVan: string | null
  periodeTot: string | null
  telling: GarageBackfillTellingDTO
  facturen: GarageBackfillRapportFactuurDTO[]
}

/** Resultaat van de bulk-actie "alles eenduidige bevestigen". */
export interface BulkBevestigResultaatDTO {
  bevestigd: number
  overgeslagen: { factuurNr: string; reden: string }[]
}

// ─── Zod request-schemas (gedeeld API ↔ frontend) ────────────────────────────

const isoDatum = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum in het formaat jjjj-mm-dd')

/** Handmatige aanpassingen bij bevestigen/koppelen (per set). */
export const garageOnderhoudOverridesSchema = z.object({
  type: z.enum(VOERTUIG_ONDERHOUD_TYPE).optional(),
  werkDatum: isoDatum.optional(),
  kmStand: z.number().int().min(0).max(2_000_000).nullable().optional(),
  omschrijving: z.string().max(1000).optional(),
})
export type GarageOnderhoudOverrides = z.infer<typeof garageOnderhoudOverridesSchema>

/**
 * batch-68 werkplaatsfacturen (FO-341 §3.3) — een sectieclassificatie
 * corrigeren in de bevestig-dialoog (bij lage zekerheid).
 */
export const werkplaatsSectieKeuzeSchema = z.object({
  /** Sectiecode zoals op de factuur ("A"); null = de sectie zonder code. */
  code: z.string().trim().max(20).nullable(),
  classificatie: onderhoudRegelClassificatieSchema,
})
export type WerkplaatsSectieKeuze = z.infer<typeof werkplaatsSectieKeuzeSchema>

/** Werkvoorraad-koppel-actie: kies een voertuig óf een bestaande logregel. */
export const garageWerkvoorraadKoppelSchema = z
  .object({
    voertuigId: z.string().min(1).optional(),
    /** Koppel aan een bestaande logregel (deelfactuur/handmatige regel). */
    bestaandeOnderhoudId: z.string().min(1).optional(),
    overrides: garageOnderhoudOverridesSchema.optional(),
    // ── batch-68 werkplaatsfacturen (FO-341 §3.6) — de bevestig-dialoog per reden ──
    /**
     * Bij een kenteken/VIN-conflict: bevestig dat het gekozen voertuig klopt
     * ondanks het afwijkende chassisnummer (de API dwingt dit af bij die reden).
     */
    vinAfwijkingBevestigd: z.boolean().optional(),
    /** Het lege chassisnummer van het gekozen voertuig vullen met het VIN van de factuur. */
    chassisNummerOvernemen: z.boolean().optional(),
    /** Sectieclassificaties corrigeren (bij lage zekerheid). */
    secties: z.array(werkplaatsSectieKeuzeSchema).max(50, 'Maximaal 50 secties').optional(),
  })
  .refine((d) => Boolean(d.voertuigId) || Boolean(d.bestaandeOnderhoudId), {
    message: 'Kies een voertuig of een bestaande onderhoudsregel',
    path: ['voertuigId'],
  })
export type GarageWerkvoorraadKoppelInput = z.infer<typeof garageWerkvoorraadKoppelSchema>

/** Werkvoorraad: expliciet "geen STREEP-voertuig" (privé/onbekend kenteken). */
export const garageWerkvoorraadGeenVoertuigSchema = z.object({
  notitie: z.string().min(3, 'Licht kort toe (min. 3 tekens)').max(500),
})
export type GarageWerkvoorraadGeenVoertuigInput = z.infer<
  typeof garageWerkvoorraadGeenVoertuigSchema
>

/** Backfill-run starten (periode-filter op factuurdatum; leeg = alles). */
export const garageBackfillStartSchema = z
  .object({
    periodeVan: isoDatum.nullable().optional(),
    periodeTot: isoDatum.nullable().optional(),
  })
  .refine(
    (d) => !d.periodeVan || !d.periodeTot || d.periodeVan <= d.periodeTot,
    { message: 'Periode-van moet vóór periode-tot liggen', path: ['periodeVan'] },
  )
export type GarageBackfillStartInput = z.infer<typeof garageBackfillStartSchema>

/** Eén set binnen een backfill-bevestiging (keuze/override per kenteken). */
export const garageBackfillSetKeuzeSchema = z.object({
  /** De genormaliseerde set-sleutel uit de preview ('' = geen kenteken). */
  kenteken: z.string(),
  /** Overslaan van deze set binnen de factuur. */
  overslaan: z.boolean().optional(),
  /** Voertuig-keuze (verplicht wanneer de preview geen eenduidige match had). */
  voertuigId: z.string().min(1).optional(),
  /** Koppel aan een bestaande logregel i.p.v. een nieuwe aan te maken. */
  bestaandeOnderhoudId: z.string().min(1).optional(),
  overrides: garageOnderhoudOverridesSchema.optional(),
})

/** Backfill: één factuur-rij bevestigen (registreert — vereist previewToken). */
export const garageBackfillBevestigSchema = z.object({
  previewToken: z.string().min(1),
  sets: z.array(garageBackfillSetKeuzeSchema).optional(),
})
export type GarageBackfillBevestigInput = z.infer<typeof garageBackfillBevestigSchema>

export const garageBackfillOverslaanSchema = z.object({
  reden: z.string().min(3, 'Geef een reden (min. 3 tekens)').max(500),
})
export type GarageBackfillOverslaanInput = z.infer<typeof garageBackfillOverslaanSchema>

export const garageBackfillAnnulerenSchema = z.object({
  reden: z.string().min(5, 'Geef een reden (min. 5 tekens)').max(500),
})
export type GarageBackfillAnnulerenInput = z.infer<typeof garageBackfillAnnulerenSchema>

/** APK-vervaldatum-voorstel op een logregel bevestigen of afwijzen. */
export const garageApkVoorstelBesluitSchema = z.object({
  besluit: z.enum(['BEVESTIG', 'WIJS_AF']),
})
export type GarageApkVoorstelBesluitInput = z.infer<typeof garageApkVoorstelBesluitSchema>

// ─── Leverancier-inkoopinstellingen (garage-vlag + FO-147/320-defaults) ──────

/**
 * PATCH /inkoop/leveranciers/:id/instellingen — de garage-vlag is een
 * beheer-actie op de leverancier (geen hardcoded namen in code). De FO-147/
 * FO-320-defaults (kostensoort/doeltype) hadden nog geen schrijfpad en worden
 * hier meegenomen (één instellingen-card in de UI).
 */
export const leverancierInkoopInstellingenSchema = z.object({
  isWerkplaats: z.boolean().optional(),
  // batch-65 (FO-483): alleen de interne dragers; een klant is geen leveranciers-default.
  defaultDoeltype: z.enum(KOSTENDRAGER_DOELTYPE).nullable().optional(),
  // batch-65 inkoop-kosten (FO-482) — leveranciersdefault uit de stamtabel.
  defaultKostensoortId: idParam('defaultKostensoortId').nullable().optional(),
  // batch-68 werkplaatsfacturen (FO-341 §3.1) — ons debiteurnummer bij deze
  // leverancier (herkenningssleutel) en de vaste inkoop-betaaltermijn. Leeg
  // (null) = uit; zonder termijn geldt het bestaande gedrag (PDF-vervaldatum).
  debiteurNummerBijLeverancier: z
    .string()
    .trim()
    .max(50, 'Maximaal 50 tekens')
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v && v.length > 0 ? v : null)),
  inkoopBetaaltermijnDagen: z
    .number({ message: 'Vul een aantal dagen in' })
    .int('Gebruik een heel aantal dagen')
    .min(1, 'Minimaal 1 dag')
    .max(120, 'Maximaal 120 dagen')
    .nullable()
    .optional(),
})
export type LeverancierInkoopInstellingenInput = z.infer<
  typeof leverancierInkoopInstellingenSchema
>

export interface LeverancierInkoopInstellingenDTO {
  isWerkplaats: boolean
  defaultDoeltype: KostendragerDoeltype | null
  // batch-65 inkoop-kosten (FO-482) — leveranciersdefault uit de stamtabel.
  defaultKostensoortId: string | null
  defaultKostensoort: KostensoortRefDTO | null
  // batch-68 werkplaatsfacturen (FO-341 §3.1)
  debiteurNummerBijLeverancier: string | null
  inkoopBetaaltermijnDagen: number | null
}
